import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Plus, FlowArrow, PencilSimple, Trash, Play, Tag as TagIcon,
  CheckCircle, XCircle, Link as LinkIcon, Timer, ArrowClockwise, Circuitry,
} from "@phosphor-icons/react";

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function TagChip({ tag, active, onClick, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`wf-tag-chip-${tag}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${
        active
          ? "border-white/40 bg-white text-graphite-950"
          : "border-white/10 bg-graphite-800 text-ink-secondary hover:text-ink-primary hover:border-white/20"
      }`}
    >
      <TagIcon weight="duotone" className="h-3 w-3" />
      <span>{tag}</span>
      <span className={`text-[10px] ${active ? "text-graphite-950/60" : "text-ink-secondary/70"}`}>· {count}</span>
    </button>
  );
}

function StatusPill({ status }) {
  const ok = status === "success";
  return (
    <span
      data-testid={`wf-run-status-${status}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] border ${
        ok
          ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
          : "border-red-400/30 bg-red-400/10 text-red-300"
      }`}
    >
      {ok ? <CheckCircle weight="fill" className="h-3 w-3" /> : <XCircle weight="fill" className="h-3 w-3" />}
      {ok ? "Success" : "Failed"}
    </span>
  );
}

const emptyForm = { name: "", description: "", webhook_url: "", tag: "", category_id: "", linked_to: "", report_token: "", workflow_id: "" };

export default function Workflows() {
  const { user, currentOrg } = useAuth();
  const isPlatformAdmin = !!user?.is_platform_admin;
  const [workflows, setWorkflows] = useState([]);
  const [runs, setRuns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState(null);
  const [activeCategory, setActiveCategory] = useState("__all__");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // workflow_id or null
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null); // workflow object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, rRes, cRes] = await Promise.all([
        api.get("/workflows"),
        api.get("/workflows/runs"),
        api.get("/automation-categories"),
      ]);
      setWorkflows(wRes.data);
      setRuns(rRes.data);
      setCategories(cRes.data);
    } catch {
      toast.error("Could not load workflows");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  // Human-readable label for a workflow's category (managed first, legacy tag
  // fallback, then Uncategorized).
  const wfCategoryLabel = (w) => {
    if (w.category_name) return w.category_name;
    if (w.tag && w.tag.trim()) return `${w.tag} (legacy)`;
    return "Uncategorized";
  };

  const categoryChips = useMemo(() => {
    const map = new Map();
    workflows.forEach((w) => {
      const label = wfCategoryLabel(w);
      map.set(label, (map.get(label) || 0) + 1);
    });
    return Array.from(map.entries()).map(([label, count]) => ({ label, count }));
  }, [workflows]);

  const filteredWorkflows = useMemo(() => {
    if (activeCategory === "__all__") return workflows;
    return workflows.filter((w) => wfCategoryLabel(w) === activeCategory);
  }, [workflows, activeCategory]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };
  const openEdit = (w) => {
    setEditing(w.workflow_id);
    setForm({
      name: w.name || "",
      description: w.description || "",
      webhook_url: w.webhook_url || "",
      tag: w.tag || "",
      category_id: w.category_id || "",
      linked_to: w.linked_to || "",
      report_token: w.report_token || "",
      workflow_id: w.workflow_id || "",
    });
    setDialogOpen(true);
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const reportUrl = form.report_token && form.workflow_id
    ? `${backendUrl}/api/workflows/${form.workflow_id}/report/${form.report_token}`
    : "";

  const copyReportUrl = async () => {
    try {
      await navigator.clipboard.writeText(reportUrl);
      toast.success("Report URL copied");
    } catch { toast.error("Could not copy"); }
  };

  const regenerateToken = async () => {
    if (!editing) return;
    if (!window.confirm("Regenerate the report token? The old URL will stop working immediately.")) return;
    try {
      const { data } = await api.post(`/workflows/${editing}/regenerate-token`);
      setForm((f) => ({ ...f, report_token: data.report_token }));
      // Also update the card list in place
      setWorkflows((all) => all.map((x) =>
        x.workflow_id === editing ? { ...x, report_token: data.report_token } : x
      ));
      toast.success("New report token generated");
    } catch {
      toast.error("Could not regenerate token");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const url = form.webhook_url.trim();
    if (!name) { toast.error("Name is required"); return; }
    if (!/^https:\/\//i.test(url)) {
      toast.error("Webhook URL must start with https://");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/workflows/${editing}`, form);
        toast.success("Workflow updated");
      } else {
        await api.post("/workflows", form);
        toast.success("Workflow added");
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const wf = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/workflows/${wf.workflow_id}`);
      setWorkflows((all) => all.filter((x) => x.workflow_id !== wf.workflow_id));
      toast.success(`"${wf.name}" removed`);
    } catch {
      toast.error("Could not delete workflow");
    }
  };

  const handleRun = async (wf) => {
    if (runningId) return;
    setRunningId(wf.workflow_id);
    try {
      const { data } = await api.post(`/workflows/${wf.workflow_id}/run`, {}, { timeout: 15000 });
      setRuns((r) => [data, ...r].slice(0, 500));
      if (data.status === "success") {
        toast.success(`"${wf.name}" triggered — ${data.http_status || "OK"} in ${data.duration_ms}ms`);
      } else {
        toast.error(`"${wf.name}" failed — ${data.error_message || "unknown error"}`);
      }
    } catch (err) {
      toast.error(`Could not trigger "${wf.name}"`);
    } finally {
      setRunningId(null);
    }
  };

  return (
    <div className="p-8 md:p-10 max-w-[1400px]" data-testid="workflows-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">
            {currentOrg?.name || "Workspace"}
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Workflows</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-lg">
            Trigger your organization&rsquo;s n8n automations from one panel. The logic lives in n8n; this is just the control room.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="workflows-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 transition-colors"
          >
            <ArrowClockwise weight="bold" className="h-4 w-4" />
            Refresh
          </button>
          <button
            data-testid="workflows-add-button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors"
          >
            <Plus weight="bold" className="h-4 w-4" />
            New workflow
          </button>
        </div>
      </div>

      <Tabs defaultValue="registry" className="mt-8">
        <TabsList
          data-testid="workflows-tabs"
          className="bg-graphite-800 border border-white/10 p-1 rounded-md"
        >
          <TabsTrigger
            value="registry"
            data-testid="workflows-tab-registry"
            className="data-[state=active]:bg-white data-[state=active]:text-graphite-950 rounded-sm px-3 py-1.5 text-sm text-ink-secondary"
          >
            Registry
          </TabsTrigger>
          <TabsTrigger
            value="runs"
            data-testid="workflows-tab-runs"
            className="data-[state=active]:bg-white data-[state=active]:text-graphite-950 rounded-sm px-3 py-1.5 text-sm text-ink-secondary"
          >
            Run log
            <span className="ml-2 text-[10px] rounded-full bg-graphite-900 px-1.5 py-0.5 text-ink-secondary/80">
              {runs.length}
            </span>
          </TabsTrigger>
        </TabsList>

        {/* REGISTRY */}
        <TabsContent value="registry" className="mt-6 focus-visible:outline-none">
          {/* Category filter */}
          {categoryChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="workflows-category-filter">
              <button
                type="button"
                onClick={() => setActiveCategory("__all__")}
                data-testid="wf-cat-chip-__all__"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${
                  activeCategory === "__all__"
                    ? "border-white/40 bg-white text-graphite-950"
                    : "border-white/10 bg-graphite-800 text-ink-secondary hover:text-ink-primary hover:border-white/20"
                }`}
              >
                All
                <span className={`text-[10px] ${activeCategory === "__all__" ? "text-graphite-950/60" : "text-ink-secondary/70"}`}>
                  · {workflows.length}
                </span>
              </button>
              {categoryChips.map(({ label, count }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setActiveCategory(label)}
                  data-testid={`wf-cat-chip-${label}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${
                    activeCategory === label
                      ? "border-white/40 bg-white text-graphite-950"
                      : "border-white/10 bg-graphite-800 text-ink-secondary hover:text-ink-primary hover:border-white/20"
                  }`}
                >
                  <Circuitry weight="duotone" className="h-3 w-3" />
                  <span>{label}</span>
                  <span className={`text-[10px] ${activeCategory === label ? "text-graphite-950/60" : "text-ink-secondary/70"}`}>· {count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Workflow cards */}
          <div className="mt-6">
            {loading ? (
              <div className="rounded-md border border-white/10 bg-graphite-900 p-10 text-center text-sm text-ink-secondary">
                Loading workflows…
              </div>
            ) : filteredWorkflows.length === 0 ? (
              <div className="rounded-md border border-white/10 bg-graphite-900 p-12 text-center" data-testid="workflows-empty">
                <div className="mx-auto h-12 w-12 rounded-md border border-dashed border-white/15 grid place-items-center">
                  <FlowArrow weight="duotone" className="h-5 w-5 text-ink-secondary" />
                </div>
                <div className="mt-4 font-display text-xl tracking-tight">
                  {workflows.length === 0 ? "No workflows yet" : "Nothing here for that category"}
                </div>
                <p className="mt-2 text-sm text-ink-secondary max-w-sm mx-auto">
                  {workflows.length === 0
                    ? "Register your first n8n webhook to trigger it from here."
                    : "Try a different category or clear the filter."}
                </p>
                {workflows.length === 0 && (
                  <button
                    onClick={openCreate}
                    data-testid="workflows-empty-add"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-5 py-2.5 text-sm font-medium hover:bg-neutral-200 transition-colors"
                  >
                    <Plus weight="bold" className="h-4 w-4" />
                    Add workflow
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredWorkflows.map((w) => (
                  <article
                    key={w.workflow_id}
                    data-testid={`wf-card-${w.workflow_id}`}
                    className="rounded-md border border-white/10 bg-graphite-900 p-5 flex flex-col hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 shrink-0 rounded-sm bg-graphite-800 border border-white/10 grid place-items-center">
                        <FlowArrow weight="duotone" className="h-4 w-4 text-ink-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-display text-lg tracking-tight truncate" data-testid={`wf-name-${w.workflow_id}`}>
                          {w.name}
                        </h3>
                        {(w.category_name || w.tag) && (
                          <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-ink-secondary" data-testid={`wf-cat-label-${w.workflow_id}`}>
                            {w.category_name ? (
                              <>
                                <Circuitry weight="duotone" className="h-3 w-3" />
                                {w.category_name}
                              </>
                            ) : (
                              <>
                                <TagIcon weight="duotone" className="h-3 w-3" />
                                {w.tag}
                                <span className="opacity-60">· legacy</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {w.description && (
                      <p className="mt-3 text-sm text-ink-secondary line-clamp-3">{w.description}</p>
                    )}
                    <div className="mt-4 space-y-1.5 text-[11px] text-ink-secondary">
                      <div className="flex items-center gap-1.5 truncate">
                        <LinkIcon weight="duotone" className="h-3 w-3 shrink-0" />
                        <span className="truncate" title={w.webhook_url}>{w.webhook_url}</span>
                      </div>
                      {w.linked_to && (
                        <div className="truncate">
                          <span className="opacity-60">Linked to · </span>
                          <span className="text-ink-primary/80">{w.linked_to}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-5 flex items-center gap-2">
                      {isPlatformAdmin && (
                        <button
                          data-testid={`wf-run-${w.workflow_id}`}
                          onClick={() => handleRun(w)}
                          disabled={runningId === w.workflow_id}
                          className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 disabled:opacity-60 transition-colors"
                        >
                          <Play weight="fill" className="h-3.5 w-3.5" />
                          {runningId === w.workflow_id ? "Running…" : "Run now"}
                        </button>
                      )}
                      <button
                        data-testid={`wf-edit-${w.workflow_id}`}
                        onClick={() => openEdit(w)}
                        className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 transition-colors"
                        title="Edit"
                      >
                        <PencilSimple weight="bold" className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid={`wf-delete-${w.workflow_id}`}
                        onClick={() => setConfirmDelete(w)}
                        className="ml-auto inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-sm text-ink-secondary hover:text-accent-danger hover:bg-white/5 transition-colors"
                        title="Delete"
                      >
                        <Trash weight="bold" className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* RUN LOG */}
        <TabsContent value="runs" className="mt-6 focus-visible:outline-none">
          <div className="rounded-md border border-white/10 bg-graphite-900 overflow-hidden" data-testid="wf-runs-panel">
            {loading ? (
              <div className="p-10 text-center text-sm text-ink-secondary">Loading run log…</div>
            ) : runs.length === 0 ? (
              <div className="p-12 text-center" data-testid="wf-runs-empty">
                <div className="mx-auto h-12 w-12 rounded-md border border-dashed border-white/15 grid place-items-center">
                  <Timer weight="duotone" className="h-5 w-5 text-ink-secondary" />
                </div>
                <div className="mt-4 font-display text-xl tracking-tight">No runs yet</div>
                <p className="mt-2 text-sm text-ink-secondary">Trigger any workflow to see it here.</p>
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {runs.map((r) => {
                  const isAuto = r.source === "automatic";
                  return (
                  <li key={r.run_id} className="p-4 flex items-center gap-4" data-testid={`wf-run-row-${r.run_id}`}>
                    <StatusPill status={r.status} />
                    <span
                      data-testid={`wf-run-source-${r.run_id}`}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest border ${
                        isAuto
                          ? "border-sky-400/30 bg-sky-400/10 text-sky-300"
                          : "border-white/10 bg-graphite-950 text-ink-secondary"
                      }`}
                    >
                      {isAuto ? "Automatic" : "Manual"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-ink-primary truncate" data-testid={`wf-run-name-${r.run_id}`}>
                        {r.workflow_name}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-ink-secondary">
                        <span>{fmtDateTime(r.triggered_at)}</span>
                        <span className="opacity-40">·</span>
                        <span>by {r.triggered_by_name || "—"}</span>
                        {r.duration_ms != null && (<>
                          <span className="opacity-40">·</span>
                          <span className="inline-flex items-center gap-1">
                            <Timer weight="bold" className="h-3 w-3" />
                            {r.duration_ms}ms
                          </span>
                        </>)}
                        {r.http_status && (<><span className="opacity-40">·</span><span>HTTP {r.http_status}</span></>)}
                        {r.error_message && r.status !== "success" && (
                          <>
                            <span className="opacity-40">·</span>
                            <span className="text-red-300/90 truncate">{r.error_message}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="border-white/10 bg-graphite-800 text-ink-primary max-w-lg"
          data-testid="wf-form-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display font-light tracking-tight text-2xl">
              {editing ? "Edit workflow" : "New workflow"}
            </DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Register an n8n webhook. This app only triggers it — the automation logic lives in n8n.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Name</label>
              <Input
                data-testid="wf-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Onboard new customer"
                className="bg-graphite-900 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
                required
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Description</label>
              <Textarea
                data-testid="wf-form-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="What this workflow does…"
                className="bg-graphite-900 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Webhook URL (https)</label>
              <Input
                data-testid="wf-form-webhook"
                value={form.webhook_url}
                onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
                placeholder="https://n8n.example.com/webhook/abcd-1234"
                className="bg-graphite-900 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary font-mono text-xs"
                required
              />
              <p className="text-[11px] text-ink-secondary/70">Must start with https://</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Category</label>
                <select
                  data-testid="wf-form-category"
                  value={form.category_id}
                  onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                  className="w-full h-9 rounded-md bg-graphite-900 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/30"
                >
                  <option value="">Uncategorized</option>
                  {categories.map((c) => (
                    <option key={c.category_id} value={c.category_id}>{c.name}</option>
                  ))}
                </select>
                <p className="text-[11px] text-ink-secondary/70">Pick from ApexGrey&rsquo;s managed automation categories.</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Linked to (free text)</label>
                <Input
                  data-testid="wf-form-linked"
                  value={form.linked_to}
                  onChange={(e) => setForm({ ...form, linked_to: e.target.value })}
                  placeholder="Customer, Invoice, …"
                  className="bg-graphite-900 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
                />
              </div>
            </div>

            <DialogFooter>
              <button
                type="button"
                data-testid="wf-form-cancel"
                onClick={() => setDialogOpen(false)}
                className="rounded-md border border-white/10 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="wf-form-submit"
                disabled={saving}
                className="rounded-md bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 disabled:opacity-50 transition-colors"
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Add workflow"}
              </button>
            </DialogFooter>
          </form>

          {editing && reportUrl && (
            <div className="mt-2 pt-4 border-t border-white/10" data-testid="wf-report-section">
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-2">
                Report-back URL
              </div>
              <p className="mt-1 text-[11px] text-ink-secondary/80">
                Add an HTTP Request node at the end of your n8n workflow pointing to this URL to log automatic runs here.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <input
                  data-testid="wf-report-url"
                  readOnly
                  value={reportUrl}
                  className="flex-1 bg-graphite-900 border border-white/10 rounded-md px-3 py-2 text-[11px] text-ink-primary font-mono"
                  onClick={(e) => e.target.select()}
                />
                <button
                  type="button"
                  data-testid="wf-report-copy"
                  onClick={copyReportUrl}
                  className="rounded-md border border-white/10 px-3 py-2 text-xs hover:bg-white/5"
                  title="Copy URL"
                >
                  Copy
                </button>
                <button
                  type="button"
                  data-testid="wf-report-regenerate"
                  onClick={regenerateToken}
                  className="rounded-md border border-white/10 px-3 py-2 text-xs text-amber-300 hover:bg-amber-400/5"
                  title="Regenerate token (invalidates the old URL)"
                >
                  Regenerate
                </button>
              </div>
              <p className="mt-2 text-[10px] text-ink-secondary/60">
                POST JSON like {'{"status":"success"}'} or {'{"status":"failure","message":"..."}'} to this URL. No other auth required — the token IS the auth.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-graphite-800 text-ink-primary" data-testid="wf-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-light tracking-tight text-2xl">
              Delete workflow?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary text-sm">
              &ldquo;{confirmDelete?.name}&rdquo; will be removed from this workspace. This action can&rsquo;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="wf-delete-cancel"
              className="border-white/10 bg-transparent hover:bg-white/5 text-ink-primary"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="wf-delete-confirm"
              onClick={handleDelete}
              className="bg-accent-danger text-graphite-950 hover:bg-red-300"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
